import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, FormGroupDirective} from '@angular/forms';
import {IDocumentTypesDto} from 'app/main/pages/documents/dto/document-listing.dto';

import {Subject} from 'rxjs';
import { GlobalService } from 'app/global/global.service';
import { IGetCategoriesItemDto } from 'app/main/pages/categories.dto';
import { ICategory as ICategoryField } from 'app/main/pages/settings/documenttype/document-type-settings-modal/document-type-settings.dto';

@Component({
    selector: 'app-upload-file',
    templateUrl: './upload-file.component.html',
    styleUrls: ['./upload-file.component.scss'],
})
export class UploadFileComponent implements OnInit, OnDestroy {
    @Input({required: true}) file: File;
    @Input() progress: number = 0;
    @Input() documentTypes: IDocumentTypesDto[];
    @Input() categories: IGetCategoriesItemDto[];
    @Input() category: number = null;
    @Input() documentTypeId: number = null;
    @Input() formGroupName: number;

    @Output() delete: EventEmitter<File> = new EventEmitter();
    @Output() categoryChange: EventEmitter<number[]> = new EventEmitter();
    @Output() documentTypeIdChange: EventEmitter<number> = new EventEmitter();

    public form: FormGroup;
    public selectedDocumentType: IDocumentTypesDto;
    public flattenedCategories: Partial<IGetCategoriesItemDto>[] = [];
    public categoryFields: ICategoryField[] = [];
    private onDestroy$: Subject<void> = new Subject<void>();

    constructor(
        private rootFormGroup: FormGroupDirective,
        private _globalService: GlobalService
    ) {

    }

    ngOnInit(): void {
        this.flattenCategories()
        this.form = (this.rootFormGroup.control?.get('files') as FormGroup).controls[this.formGroupName] as FormGroup;

    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    onDelete(): void {
        this.delete.next(this.file);
    }

    onDocumentTypeChange(documentType: IDocumentTypesDto) {
        this.selectedDocumentType = documentType;
        this.categoryFields = [];
        
        // Extract category fields from document type
        if (documentType) {
            documentType.sections?.forEach(section => {
                if (section.name === 'document_category') {
                    section.categories?.forEach(categoryField => {
                        if ((categoryField.status) && (!categoryField?.category?.parent_id || categoryField?.category?.parent_id == 0)) {
                            this.categoryFields.push(categoryField);
                        }
                    });
                }
            });
        }
        
        // Clear selected categories when document type changes
        this.form.get('category_ids')?.setValue([]);
    }

    private getAllowedCategoryIdsForDocumentType(documentType: IDocumentTypesDto): number[] {
        const allowedIds: number[] = [];
        
        // Get category fields from document type sections
        documentType?.sections?.forEach(section => {
            if (section.name === 'document_category') {
                section.categories?.forEach(categoryField => {
                    if (categoryField.status) {
                        // Add all IDs from this category tree
                        this.addCategoryTreeIds(categoryField.category, allowedIds);
                    }
                });
            }
        });
        
        return allowedIds;
    }

    private addCategoryTreeIds(category: any, ids: number[]): void {
        if (category?.id) {
            ids.push(category.id);
        }
        
        const children = category?.children || category?.sub_category;
        if (children && Array.isArray(children)) {
            children.forEach(child => this.addCategoryTreeIds(child, ids));
        }
    }

    getSelectedCategories(){
        let selectedCategories = [];
        const selectedIds = this.form.get('category_ids')?.value || [];
        if (selectedIds && selectedIds.length > 0) {
            selectedCategories = this.flattenedCategories.filter(x => selectedIds.includes(x?.id));
        }
        return selectedCategories;
    }

    onCategorySelectionChange(newIds: number[]): void {
        this.form.get('category_ids')?.setValue(newIds);
        this.categoryChange.emit(newIds);
    }

    handleCategoryFieldChange(newIds: number[], field: ICategoryField): void {
        const controlName = this.getCategoryControlName(field);
        
        // Update individual control - replace instead of merge
        if (!this.form.get(controlName)) {
            this.form.addControl(controlName, new FormControl(newIds));
        } else {
            this.form.get(controlName)?.setValue(newIds);
        }
        
        // Aggregate all category IDs
        this.updateAggregateCategoryIds();
    }

    getCategoryControlName(field: ICategoryField): string {
        return `category_${field.category.id}`;
    }

    getCategoryControlValue(field: ICategoryField): number[] {
        const controlName = this.getCategoryControlName(field);
        return this.form.get(controlName)?.value ?? [];
    }

    private updateAggregateCategoryIds(): void {
        let allIds: number[] = [];
        this.categoryFields.forEach(field => {
            const controlName = this.getCategoryControlName(field);
            const val = this.form.get(controlName)?.value;
            if (Array.isArray(val)) {
                allIds.push(...val);
            }
        });
        // Remove duplicates
        allIds = [...new Set(allIds)];
        this.form.get('category_ids')?.setValue(allIds);
        this.categoryChange.emit(allIds);
    }

    flattenCategories(): void {
        this.flattenedCategories = [];
        const categories = this.categories;
        categories?.forEach(category => {
            this.flattenedCategories.push(category);
            if (category.sub_category && category.sub_category.length > 0) {
                this.flattenedCategories.push(...category.sub_category.map(sub => ({ ...sub, is_sub: 1 })));
            }
        });
    }
}

export interface IUploadPdfFilesForm {
    file: File,
    progress: number,
    category_ids: number[],
    document_type_id: number,
    contract_card_ids: number,
}
